import { Banner } from '@mm/components';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CollapsibleContentContainerWithoutLocalState } from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainerWithoutLocalState';
import { ChatWidgetTroubleshootingTool } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/ChatWidgetTroubleshootingTool/ChatWidgetTroubleshootingTool';
import { CustomerChatConditionRulesEditor } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatConditionsEditor/CustomerChatConditionRulesEditor/CustomerChatConditionRulesEditor';
import { useCustomerChatConditionsEditorPromoterState } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatConditionsEditor/use-customer-chat-conditions-editor-state';
import { CustomerChatInstallInformation } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatInstallInformation/CustomerChatInstallInformation';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { ReactComponent as ExplainerImage } from './explainer.svg';
import styles from './GetemailsSlideoutEditor.module.css';

export type GetemailsSlideoutEditorProps = {
  promoterId: number;
};

export const GetemailsSlideoutEditor = ({
  promoterId,
}: GetemailsSlideoutEditorProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const promoters = useSelector(state =>
    selectPromotersByType(state, 'getemails'),
  );
  const promoter = promoters.find(p => p.id === promoterId);
  const promoterCRUDActions = usePromoterCRUDActions();
  const [rulesExpanded, setRulesExpanded] = useState(true);
  const [installInfoExpanded, setInstallInfoExpanded] = useState(true);
  const { setRef } = useSlideoutOnClose();

  const {
    localPromoter,
    setLocalPromoter,
    updatePromoter,
    updateLocalPromoterField,
  } = useCustomerChatConditionsEditorPromoterState(promoter);

  const promoterHasNoConditions = (promoter: PromoterModel) =>
    !promoter.conditions?.rules.length ||
    promoter.conditions.rules.every(r => r.operator === '*');

  const warnAboutMultipleRuleFreePromoters =
    promoter &&
    promoterHasNoConditions(promoter) &&
    promoters.some(p => p.id !== promoter.id && promoterHasNoConditions(p));

  const duplicateRules =
    !warnAboutMultipleRuleFreePromoters && promoter?.conditions?.rules.length
      ? promoter.conditions.rules.filter(rule =>
          promoters.some(
            p =>
              p.id !== promoter.id &&
              (p.conditions?.rules || []).some(
                otherRule =>
                  otherRule.operator === rule.operator &&
                  otherRule.value === rule.value,
              ),
          ),
        )
      : [];

  useEffect(() => {
    async function fetchItems() {
      await promoterCRUDActions.retrieveAll(chatbotId, 'getemails');
    }
    fetchItems();
  }, [chatbotId, promoterCRUDActions]);

  if (!promoter) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader
        className={styles.header}
        promoter={promoter}
        title="Web Visitor Email Detector"
        deleteWithPromoterCRUDAction
      />
      <SlideoutMenu.Content className={styles.content}>
        <Banner
          css={{
            container: styles.bannerContainer,
            header: styles.bannerHeader,
            collapsedHeader: styles.collapsedHeader,
            content: styles.bannerContent,
          }}
          withBannerHeader
          headerText="What is Email Address of Visitor Detected?"
          withLeftArea={false}
          showCloseIcon
          usedInSlideout
        >
          <ExplainerImage />
        </Banner>
        <CollapsibleContentContainerWithoutLocalState
          orderNumber="1"
          containerName="Specify what pages on your Website should trigger this automation"
          className={styles.collapsibleContainer}
          childrenContainerClassName={styles.collapsibleContainerDropdown}
          stepperStylingPlatform="webchat"
          useCircleNumberStyle
          isExpanded={rulesExpanded}
          toggleIsExpanded={setRulesExpanded}
        >
          <CustomerChatConditionRulesEditor
            usedInSlideout
            updateLocalPromoterField={updateLocalPromoterField}
            updatePromoter={updatePromoter}
            localPromoter={localPromoter}
            setLocalPromoter={setLocalPromoter}
            warnAboutMultipleRuleFreePromoters={
              warnAboutMultipleRuleFreePromoters
            }
            duplicateRules={duplicateRules}
          />
        </CollapsibleContentContainerWithoutLocalState>

        <CollapsibleContentContainerWithoutLocalState
          orderNumber="2"
          containerName="Install Website Script"
          className={styles.collapsibleContainer}
          childrenContainerClassName={styles.collapsibleContainerDropdown}
          stepperStylingPlatform="webchat"
          useCircleNumberStyle
          isExpanded={installInfoExpanded}
          toggleIsExpanded={setInstallInfoExpanded}
        >
          <CustomerChatInstallInformation
            promoter={promoter}
            usedInSlideout
            hideWhitelist
          />
        </CollapsibleContentContainerWithoutLocalState>
        <CollapsibleContentContainerWithoutLocalState
          orderNumber="3"
          containerName="Verify the script is working properly"
          className={styles.collapsibleContainer}
          childrenContainerClassName={styles.collapsibleContainerDropdown}
          stepperStylingPlatform="webchat"
          useCircleNumberStyle
          isExpanded={installInfoExpanded}
          toggleIsExpanded={setInstallInfoExpanded}
        >
          <ChatWidgetTroubleshootingTool
            promoter={promoter}
            usedInSlideout
            isXRay
          />
        </CollapsibleContentContainerWithoutLocalState>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
